import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { scrollToSection } from "./Navbar";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onQualify }) => {
  const { scrollY } = useScroll();
  const bandY = useTransform(scrollY, [0, 900], [0, 80]);
  const badgeY = useTransform(scrollY, [0, 900], [0, -60]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-[#060D1A] pb-20 pt-32 sm:pt-40">
      <div className="grain-overlay" />
      <div className="pointer-events-none absolute -left-40 top-0 h-[480px] w-[480px] rounded-full bg-[#132342] blur-[130px]" />
      <div className="pointer-events-none absolute -right-32 top-48 h-[380px] w-[380px] rounded-full bg-[#DC2626]/15 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={12} className="fill-[#F59E0B] text-[#F59E0B]" />
            ))}
          </span>
          <span className="h-px w-10 bg-[#F59E0B]/70" />
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#FEF3C7] sm:text-xs">
            A 1% Lender Credit for Georgia Local Heroes
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-[#FAF8F5] sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.2}>Georgia</MaskedLine>
          <MaskedLine delay={0.35}>
            <span className="text-[#DC2626]">Local</span> <span className="text-[#F59E0B]">Heroes</span>
          </MaskedLine>
          <MaskedLine delay={0.55} className="mt-4">
            <span className="font-body text-base font-medium normal-case italic tracking-normal text-[#FEF3C7]/90 sm:text-lg">
              Serving those who serve Georgia.
            </span>
          </MaskedLine>
        </h1>

        <motion.div
          style={{ y: badgeY }}
          className="absolute right-6 top-2 hidden sm:block lg:right-12"
          data-testid="hero-credit-badge-desktop"
        >
          <div className="relative h-48 w-48 lg:h-56 lg:w-56">
            <svg viewBox="0 0 100 100" className="badge-spin absolute inset-0 h-full w-full">
              <defs>
                <path id="badge-circle-lg" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
              </defs>
              <text style={{ fontSize: "7.6px", letterSpacing: "1.6px", fontFamily: "'JetBrains Mono', monospace", fill: "#FEF3C7" }}>
                <textPath href="#badge-circle-lg" textLength="236">
                  1% LENDER CREDIT ★ GEORGIA HEROES ★
                </textPath>
              </text>
            </svg>
            <div className="absolute inset-[24%] flex items-center justify-center rounded-full bg-[#DC2626] shadow-[0_10px_40px_rgba(220,38,38,0.55)] ring-2 ring-[#F59E0B]">
              <span className="font-display text-4xl font-black text-[#FEF3C7] lg:text-5xl">1%</span>
            </div>
          </div>
        </motion.div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <motion.button
            data-testid="hero-qualify-btn"
            onClick={onQualify}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
            className="group flex items-center gap-3 rounded-full bg-[#DC2626] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#B91C1C] hover:shadow-[0_14px_40px_rgba(220,38,38,0.5)]"
          >
            Submit Your Interest
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </motion.button>
          <motion.button
            data-testid="hero-savings-btn"
            onClick={() => scrollToSection("#savings")}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="rounded-full border border-[#F59E0B]/50 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#FEF3C7] transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#F59E0B] hover:bg-[#F59E0B]/10"
          >
            Calculate Your Savings
          </motion.button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 1.1, ease: EASE }}
          className="relative mt-44 sm:mt-16"
        >
          <motion.div
            style={{ y: bandY }}
            className="hero-clip relative overflow-hidden border-y-2 border-[#F59E0B]/40 shadow-[0_40px_120px_rgba(0,0,0,0.5)]"
          >
            <img
              src="/assets/heroes-band.png"
              alt="Georgia's educators, firefighters, healthcare professionals and military members"
              data-testid="hero-image"
              className="h-52 w-full object-cover object-top sm:h-72 lg:h-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060D1A]/60 via-transparent to-[#060D1A]/10" />
          </motion.div>

          <motion.div style={{ y: badgeY }} className="absolute -top-40 right-4 sm:hidden" data-testid="hero-credit-badge">
            <div className="relative h-40 w-40 sm:h-56 sm:w-56">
              <svg viewBox="0 0 100 100" className="badge-spin absolute inset-0 h-full w-full">
                <defs>
                  <path id="badge-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text style={{ fontSize: "7.6px", letterSpacing: "1.6px", fontFamily: "'JetBrains Mono', monospace", fill: "#FEF3C7" }}>
                  <textPath href="#badge-circle" textLength="236">
                    1% LENDER CREDIT ★ GEORGIA HEROES ★
                  </textPath>
                </text>
              </svg>
              <div className="absolute inset-[24%] flex items-center justify-center rounded-full bg-[#DC2626] shadow-[0_10px_40px_rgba(220,38,38,0.55)] ring-2 ring-[#F59E0B]">
                <span className="font-display text-4xl font-black text-[#FEF3C7] sm:text-5xl">1%</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
          className="mt-12 flex flex-wrap gap-3"
        >
          {["1% Lender Credit*", "6 Hero Professions", "Buying or Refinancing", "Based on Loan Amount"].map((s) => (
            <span
              key={s}
              className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#FAF8F5]/80 backdrop-blur-sm sm:text-[11px]"
            >
              {s}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
